import React, { useEffect, useRef, useState } from 'react';
import StepLayout from '../components/StepLayout';
import ApproveOnPhone from '../components/ApproveOnPhone';
import { api } from '../api';
import { connectSSE } from '../sse';
import { isTerminalErrorStatus } from '../utils/recoveryState';

// B-FE-APP-APPROVAL-1 (2026-05-17): if V3 emits needs_app_approval
// during the email-change phase (LinkedIn re-prompts for confirmation
// when adding the buyer email), this Step is the active surface. The
// pre-fix behavior left the seller staring at "Finalising your
// application" with no instruction to tap Yes on their phone. The
// post-fix overlays the polished <ApproveOnPhone /> card (reused from
// Step07TwoFA) for the duration of the V3 polling window.
//
// Kill-switch via REACT_APP_DISABLE_APP_APPROVAL_OVERLAY=1 falls back
// to the original "Finalising" spinner. Pattern mirrors PR #356's
// PRECHECK_LEGACY_CLASSIFIER kill-switch.
const APPROVAL_OVERLAY_DISABLED =
  process.env.REACT_APP_DISABLE_APP_APPROVAL_OVERLAY === '1';

// B-APPROVAL-RESOLVED (2026-05-17): when V3 detects the seller has
// cleared a challenge (app approved on phone OR 2FA code accepted),
// it now emits `app_approval_resolved` / `2fa_resolved`. Listen for
// either and hide the overlay so the next `needs_app_approval` can
// re-show it cleanly. Setting this env var to '1' falls back to the
// previous behavior (overlay stays up across consecutive approvals
// until a final terminal event like email_change_success arrives).
const RESOLVED_EVENTS_DISABLED =
  process.env.REACT_APP_DISABLE_RESOLVED_EVENTS === '1';

// iter-26: rebuild per Finalising Application Loading Page UI Spec.
//
// Layout:
//   - Heading "Finalising your application"
//   - Subtitle "This usually takes about 30 seconds." — explicit anchor
//     so the wait doesn't feel longer than it is
//   - Spinner: 64×64 desktop / 56×56 mobile, 4px gray track, orange arc
//   - Rotating status messages (cycle every 2.5s with 300ms fade):
//       1. "Verifying your details"
//       2. "Setting up your profile"
//       3. "Almost there"
//   - "Please don't close this window" warning
//   - Trustpilot below stays (only loading screen where it stays on-brand)
//
// Reduced-motion: pulse-opacity instead of spin, instant message swap.
//
// Polling preserves iter-18 behavior: detects terminal backend error
// states and shows an error state instead.
const STATUS_MESSAGES = [
  'Verifying your details',
  'Setting up your profile',
  'Almost there',
];

const ROTATE_MS = 2500;
const FADE_MS = 300;
// B-FINALISING-WATCHDOG (2026-06-21): hard elapsed cap so a dead email-change
// leg whose status never flips off in_progress can't leave the seller on the
// Finalising spinner forever. Sized above the 18-min (1080s) Celery hard kill.
const WATCHDOG_MS = 20 * 60 * 1000;

export default function Step18Finalizing({ sessionId, onNext }) {
  const [error, setError] = useState('');
  const [failed, setFailed] = useState(null);
  const [statusIdx, setStatusIdx] = useState(0);
  const [statusVisible, setStatusVisible] = useState(true);
  // B-FE-APP-APPROVAL-1: visible when V3 emits needs_app_approval during
  // email-change (LinkedIn prompts for app confirmation on the secondary
  // email add). Cleared when V3 emits any "we're past it" event
  // (login_success / email_change_success / etc.) or when the session
  // completes via the existing emailChangeComplete poll.
  const [showApproveOverlay, setShowApproveOverlay] = useState(false);
  const [approveError, setApproveError] = useState('');
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;
  const mountedAtRef = useRef(Date.now());

  // Status-message rotation
  useEffect(() => {
    let cancelled = false;
    const tick = setInterval(() => {
      if (cancelled) return;
      setStatusVisible(false);
      setTimeout(() => {
        if (cancelled) return;
        setStatusIdx((i) => (i + 1) % STATUS_MESSAGES.length);
        setStatusVisible(true);
      }, FADE_MS);
    }, ROTATE_MS);
    return () => {
      cancelled = true;
      clearInterval(tick);
    };
  }, []);

  // B-FE-APP-APPROVAL-1: SSE subscription to needs_app_approval events
  // emitted by V3 during the email-change phase. Reused from Step06's
  // pattern but lighter — only flips overlay state, doesn't try to
  // advance steps or write session data.
  useEffect(() => {
    if (APPROVAL_OVERLAY_DISABLED) return undefined;
    if (!sessionId) return undefined;

    const sse = connectSSE(sessionId, (event) => {
      if (!event || typeof event.type !== 'string') return;
      if (event.type === 'needs_app_approval') {
        // eslint-disable-next-line no-console
        console.log(
          '[B-FE-APPROVAL-1] showing ApproveOnPhone overlay, phase=email_change',
          { sessionId, eventType: event.type },
        );
        setShowApproveOverlay(true);
        setApproveError('');
      } else if (
        !RESOLVED_EVENTS_DISABLED
        && (event.type === 'app_approval_resolved'
            || event.type === '2fa_resolved')
      ) {
        // eslint-disable-next-line no-console
        console.log(
          '[B-APPROVAL-RESOLVED] clearing overlay, phase=email_change',
          { sessionId, eventType: event.type },
        );
        setShowApproveOverlay(false);
      } else if (
        event.type === 'email_change_success'
        || event.type === 'login_success'
        || event.type === 'sync_complete'
      ) {
        // V3 cleared the approval prompt — back to the Finalising spinner.
        setShowApproveOverlay(false);
      } else if (
        typeof event.reason === 'string'
        && event.reason.startsWith('app_approval_timeout')
      ) {
        setApproveError(
          "We didn't receive an approval from your LinkedIn app. "
          + "Please open your LinkedIn mobile app to receive the next push notification.",
        );
      }
    });

    return () => {
      try { sse && sse.close && sse.close(); } catch (_) { /* noop */ }
    };
  }, [sessionId]);

  // Backend status polling
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const status = await api.getStatus(sessionId);
        if (cancelled) return;
        if (status.emailChangeComplete) {
          onNextRef.current();
          return;
        }
        if (isTerminalErrorStatus(status.status)) {
          setFailed({
            reason: status.failureReason || 'Account update could not be completed.',
          });
          return;
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Network error, retrying…');
      }
      // B-FINALISING-WATCHDOG (2026-06-21): elapsed backstop — even if /status
      // never reports a terminal status, stop the spinner after WATCHDOG_MS so
      // the seller reaches a terminal screen rather than spinning indefinitely.
      if (Date.now() - mountedAtRef.current >= WATCHDOG_MS) {
        setFailed({
          reason: 'This is taking longer than expected. Our team will review your '
            + 'application and follow up. You can safely close this window.',
        });
        return;
      }
      timer = setTimeout(poll, 3000);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  if (failed) {
    return (
      <StepLayout title="We hit an issue" variant="centered" hideTrustpilot>
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <p style={{ color: 'var(--danger)', fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
            Account update couldn&rsquo;t be completed.
          </p>
          <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.5, marginBottom: 24 }}>
            {failed.reason}
          </p>
          <p style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.5 }}>
            Please contact support. Your details have been saved and we&rsquo;ll
            follow up shortly.
          </p>
        </div>
      </StepLayout>
    );
  }

  // B-FE-APP-APPROVAL-1: when V3 needs the seller to tap Yes on their
  // phone (re-prompt during email-change), swap to the polished
  // ApproveOnPhone card instead of the generic Finalising spinner.
  // Identical visual to Step07TwoFA's app-approval branch.
  if (showApproveOverlay) {
    return (
      <StepLayout title="Approve sign-in on your phone" variant="centered">
        <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.5, marginBottom: 20, textAlign: 'center' }}>
          LinkedIn needs you to confirm the email change.
        </p>
        <ApproveOnPhone
          subtitle="We'll continue automatically once LinkedIn confirms your approval."
          error={approveError}
        />
      </StepLayout>
    );
  }

  // Trustpilot stays on this page deliberately per spec — the user is
  // captive and reading positive reviews during a load reduces drop-off.
  return (
    <StepLayout title={null}>
      <div className="finalising" role="status" aria-live="polite">
        <h1 className="finalising-heading">Finalising your application</h1>
        <p className="finalising-sub">
          This is the final step where we check everything on your LinkedIn account. It can take a few minutes. Please keep this window open.
        </p>

        <div className="finalising-spinner" aria-hidden="true" />

        <div className="finalising-status">
          <span
            className="finalising-status__text"
            style={{
              opacity: statusVisible ? 1 : 0,
              transition: `opacity ${FADE_MS}ms ease-in-out`,
            }}
          >
            {STATUS_MESSAGES[statusIdx]}
          </span>
        </div>

        <div className="finalising-warn">
          <span className="finalising-warn__icon" aria-hidden="true">&#9432;</span>
          <span>Please don&rsquo;t close this window</span>
        </div>

        {error && <p className="form-field__error" style={{ textAlign: 'center', marginTop: 16 }}>{error}</p>}
      </div>
    </StepLayout>
  );
}
